import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { api } from "../api/axios";
import { Pill } from "../components/ui";

const STATUS_TONE = { pass: "success", fail: "error", na: "neutral", unknown: "warning" };
const SEV_TONE = { high: "error", medium: "warning", low: "neutral" };
const tableWrap = { border: 1, borderColor: "divider", borderRadius: 1.5, overflowX: "auto", mb: 3.5 };
const h2 = { mb: 1.5, fontWeight: 700, fontSize: "1.05rem" };

function statusLabel(s) {
  if (s === "pass") return "עובר";
  if (s === "fail") return "נכשל";
  if (s === "na") return "לא רלוונטי";
  return "לא ידוע";
}

function severityLabel(s) {
  if (s === "high") return "גבוהה";
  if (s === "medium") return "בינונית";
  if (s === "low") return "נמוכה";
  return s || "—";
}

export default function ComplianceReport() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const theme = useTheme();
  const BackIcon = theme.direction === "rtl" ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const runId = sp.get("runId");
    const url = runId
      ? `/api/compliance/sites/${id}/history/${encodeURIComponent(runId)}`
      : `/api/compliance/sites/${id}`;
    const { data } = await api.get(url);
    setData(data);
  }, [id, sp]);

  useEffect(() => {
    load().catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, [load]);

  const dateStr = useMemo(() => new Date().toLocaleString("he-IL"), []);
  const lastUpdatedStr = useMemo(() => {
    if (!data?.lastUpdatedAt) return null;
    try {
      return new Date(data.lastUpdatedAt).toLocaleString("he-IL");
    } catch {
      return null;
    }
  }, [data?.lastUpdatedAt]);

  const snapshotCreatedStr = useMemo(() => {
    const t = data?.run?.created_at || data?.run?.createdAt;
    if (!t) return null;
    try {
      return new Date(t).toLocaleString("he-IL");
    } catch {
      return null;
    }
  }, [data?.run]);

  const print = () => window.print();

  const brandName = (data?.site?.brand_name || "").trim() || "Accss";
  const brandLogoUrl = (data?.site?.brand_logo_url || "").trim();

  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data?.items]);

  const byCategory = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const cat = String(it.category || "כללי").trim();
      const prev = m.get(cat) || { total: 0, pass: 0, fail: 0, unknown: 0, na: 0 };
      prev.total += 1;
      if (it.status === "pass") prev.pass += 1;
      else if (it.status === "fail") prev.fail += 1;
      else if (it.status === "na") prev.na += 1;
      else prev.unknown += 1;
      m.set(cat, prev);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], "he"));
  }, [items]);

  const findings = useMemo(() => {
    const sevW = (s) => (s === "high" ? 3 : s === "medium" ? 2 : 1);
    const statusW = (s) => (s === "fail" ? 3 : s === "unknown" ? 2 : s === "pass" ? 1 : 0);
    return [...items].sort((a, b) => {
      const d1 = statusW(b.status) - statusW(a.status);
      if (d1) return d1;
      const d2 = sevW(b.severity) - sevW(a.severity);
      if (d2) return d2;
      return String(a.title || "").localeCompare(String(b.title || ""), "he");
    });
  }, [items]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Skeleton variant="rounded" height={480} sx={{ maxWidth: 1000, mx: "auto" }} />;

  const pct = data.score && typeof data.score === "object" ? data.score.percent : null;

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
    <Button className="aw-print-hide" component={RouterLink} to={`/sites/${id}/compliance`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
      מרכז תאימות
    </Button>
    <Card sx={{ "@media print": { border: 0, boxShadow: "none" } }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
        <Box
          component="style"
          dangerouslySetInnerHTML={{
            __html: `
              @media print {
                .aw-print-hide { display: none !important; }
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                @page { margin: 16mm; }
              }
            `,
          }}
        />

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 0, flexGrow: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
              }}
            >
              {brandLogoUrl ? (
                <img src={brandLogoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <Typography sx={{ fontWeight: 900, fontSize: 12 }}>AW</Typography>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h1" sx={{ lineHeight: 1.15 }}>
                דוח תאימות נגישות
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {brandName}
              </Typography>
            </Box>
          </Box>
          <Button className="aw-print-hide" variant="contained" onClick={print} startIcon={<PrintOutlinedIcon />}>
            הדפסה / שמירה כ‑PDF
          </Button>
        </Box>
        <Divider sx={{ my: 2.5 }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          נוצר: <b>{dateStr}</b>
        </Typography>
        {snapshotCreatedStr ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Snapshot: <b>{snapshotCreatedStr}</b>
          </Typography>
        ) : null}
        {lastUpdatedStr ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            עודכן לאחרונה (צ’קליסט): <b>{lastUpdatedStr}</b>
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
          אתר: <b>{data.site.name}</b> · {data.site.domain} · Site Key: <b>{data.site.site_key}</b>
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }} className="aw-print-hide">
          <Pill tone="success">עובר: {data.summary.pass}</Pill>
          <Pill tone="error">נכשל: {data.summary.fail}</Pill>
          <Pill tone="warning">לא ידוע: {data.summary.unknown}</Pill>
          {pct != null ? (
            <Pill tone={pct >= 80 ? "success" : pct >= 60 ? "warning" : "error"}>
              ציון: {pct}% ({data.score.grade})
            </Pill>
          ) : null}
        </Box>

        {data.statementUrl ? (
          <Box className="aw-print-hide" sx={{ mb: 2 }}>
            <Link href={data.statementUrl} target="_blank" rel="noopener">
              הצהרת נגישות ציבורית
            </Link>
          </Box>
        ) : null}

        <Alert severity="info" sx={{ mb: 3 }}>
          זהו דוח תפעולי המבוסס על צ’קליסט (MVP). מומלץ לבצע גם בדיקות ידניות ולהיוועץ במומחה נגישות לפי הצורך.
        </Alert>

        <Typography variant="h6" component="h2" sx={h2}>
          סיכום לפי קטגוריות
        </Typography>
        <Box sx={{ ...tableWrap, overflowX: "visible" }}>
          {byCategory.map(([cat, s], i) => (
            <Stack
              key={cat}
              direction="row"
              spacing={1}
              useFlexGap
              alignItems="center"
              flexWrap="wrap"
              sx={{ px: 2, py: 1.25, borderTop: i ? 1 : 0, borderColor: "divider" }}
            >
              <Typography sx={{ fontWeight: 600, minWidth: 220, flexGrow: 1 }}>{cat}</Typography>
              <Pill tone="success">עובר: {s.pass}</Pill>
              <Pill tone="error">נכשל: {s.fail}</Pill>
              <Pill tone="warning">לא ידוע: {s.unknown}</Pill>
              <Pill tone="neutral">סה״כ: {s.total}</Pill>
            </Stack>
          ))}
        </Box>

        <Typography variant="h6" component="h2" sx={h2}>
          ממצאים מרכזיים (נכשל/לא ידוע בראש)
        </Typography>
        <TableContainer sx={tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>בדיקה</TableCell>
              <TableCell>קטגוריה</TableCell>
              <TableCell>תקן</TableCell>
              <TableCell>חומרה</TableCell>
              <TableCell>סטטוס</TableCell>
              <TableCell>עודכן</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {findings.slice(0, 12).map((it) => (
              <TableRow key={`f_${it.check_id}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {it.title}
                  </Typography>
                  {it.notes ? (
                    <Typography variant="caption" component="div">
                      הערות: {it.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{it.category || "—"}</TableCell>
                <TableCell>{it.standard_ref || "—"}</TableCell>
                <TableCell><Pill tone={SEV_TONE[it.severity] || "neutral"}>{severityLabel(it.severity)}</Pill></TableCell>
                <TableCell><Pill tone={STATUS_TONE[it.status] || "warning"}>{statusLabel(it.status)}</Pill></TableCell>
                <TableCell>
                  {it.updated_at ? (
                    <Typography variant="caption" component="div">
                      {new Date(it.updated_at).toLocaleString("he-IL")}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>

        <Typography variant="h6" component="h2" sx={h2}>
          צ’קליסט מלא
        </Typography>

        <TableContainer sx={tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>בדיקה</TableCell>
              <TableCell>קטגוריה</TableCell>
              <TableCell>תקן</TableCell>
              <TableCell>חומרה</TableCell>
              <TableCell>סטטוס</TableCell>
              <TableCell>הערות</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.check_id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {it.title}
                  </Typography>
                  {it.description ? (
                    <Typography variant="caption" component="div">
                      {it.description}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{it.category || "—"}</TableCell>
                <TableCell>{it.standard_ref || "—"}</TableCell>
                <TableCell><Pill tone={SEV_TONE[it.severity] || "neutral"}>{severityLabel(it.severity)}</Pill></TableCell>
                <TableCell><Pill tone={STATUS_TONE[it.status] || "warning"}>{statusLabel(it.status)}</Pill></TableCell>
                <TableCell>{it.notes || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>

        <Box sx={{ mt: 1, color: "text.secondary" }}>
          <Typography variant="caption">
            דוח זה נוצר מתוך Accss. מומלץ לבצע בדיקות ידניות נוספות ולהיוועץ במומחה נגישות לפי הצורך.
          </Typography>
        </Box>
      </CardContent>
    </Card>
    </Box>
  );
}


